import { ServerEvent } from "@utilitydelta/eventplanedb-client";
import ProjectWorld, { createServerEvent, ProjectEventType, type ProjectUser } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";

export const MINOR_VERSION: number = 1;

export interface UserVisit_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.UserVisit;
    userId?: string; // ID of the user visiting
    clientId: string; // ID of the user visiting
    visitTime: number; // Timestamp of the visit
}

export interface UserVisit_ProjectionResult extends BaseProjectionResult {
    kind: 'UserVisit_ProjectionResult';
    projectUser: ProjectUser; // User who visited the project
    previousVisitTime?: number; // Previous visit timestamp
}

export interface UserVisit_EventInputs extends BaseEventInput {
    kind: 'UserVisit_EventInputs';
    userId?: string; // ID of the user visiting
    clientId: string; // ID of the client visiting
    visitTime: number; // Timestamp of the visit
}

export type UserVisit_EventProcessorType = EventProcessor<ProjectEventType, UserVisit_EventInputs, UserVisit_LocalEvent, UserVisit_ProjectionResult, UserVisit_LocalEvent, ServerEvent<ProjectEventType>>;

export class UserVisit_EventProcessor implements UserVisit_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }


    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.UserVisit; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.UserVisit; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createUndoEvent = (_undoEvent: UserVisit_LocalEvent, _originalEventProjectionResult: UserVisit_ProjectionResult): boolean => {
        return false;
    }

    public projectOntoWorld = (event: UserVisit_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): UserVisit_ProjectionResult | undefined => {

        const projectuserId = event.userId ?? event.clientId;
        const entity = this.projectWorld.projectUsers.maybeGet(projectuserId);

        if (entity === undefined) {
            return undefined; // User does not exist in the project
        }

        // Verify this event is newer than the last processed event of this type
        if (entity.lastEventTime[event.tp] !== undefined && entity.lastEventTime[event.tp] > event.et) {
            return undefined;
        }

        const previousVisitTime = entity.lastSeenOn;
        entity.lastSeenOn = event.visitTime;

        // Update metadata
        entity.lastEventTime[event.tp] = event.et;
        entity.modifiedByClientId = event.ci;
        entity.modifiedByUserId = event.ui;
        entity.modifiedOn = event.et;

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('projectUsers', entity);
        }
        return !returnProjectionResults ? undefined : {
            kind: 'UserVisit_ProjectionResult',
            projectUser: entity,
            previousVisitTime
        };
    }

    public createLocalEvent = (eventToPopulate: UserVisit_LocalEvent, userInputs: UserVisit_EventInputs): boolean => {
        eventToPopulate.userId = userInputs.userId;
        eventToPopulate.clientId = userInputs.clientId;
        eventToPopulate.visitTime = userInputs.visitTime;

        return true;
    }

    public toServerEvent = async (localEvent: UserVisit_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            userId: localEvent.userId,
            clientId: localEvent.clientId,
            visitTime: localEvent.visitTime
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: UserVisit_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}